import lodashCloneDeep from "lodash/cloneDeep";
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashSlice from "lodash/slice";
import lodashMap from "lodash/map";
import dayjs from "dayjs";

import { addTileData, removeTileData } from "@/common/maps/features";
import { httpGet } from "@/services/api.service";
import { MAP_TILE_SERVER_STATIC } from "@/common/config";
import { isFeatureUnlocked } from "@/common/featureLock.js";
import { ENDPOINT_GRIDS, ENDPOINT_GRIDS_DELTA } from "@/common/constants/api";
import { encodeAllURLParameters } from "@/common/api.utilities";
import {
  GRIDS_TYPES,
  GRIDS_STABILITY_TYPES,
  GRIDS_STABILITY_LEVELS,
  GRIDS_DATA_TYPE_GDELT_ALL,
} from "@/common/constants/grids";
import {
  TILES_SOURCE_NAMES,
  TILES_SOURCE_LAYERS,
  TILES_FORECAST_SOURCE_LAYERS,
  DEFAULT_OPACITY,
  DEFAULT_OPACITY_PULSE,
  LAYER_ID_ACETATE,
} from "@/common/constants/maps";
import { getVarianceScoreColorStyle } from "@/services/grids/grids.service";

export { _getStability as getStability };
export { _getStabilityByGeohash as getStabilityByGeohash };
export { _getStabilityByCountry as getStabilityByCountry };
export { _getStabilityByCity as getStabilityByCity };
export { _getStabilityByCounty as getStabilityByCounty };
export { _getStabilityDelta as getStabilityDelta };
export { _formatFeatures as formatFeatures };
export { _formatFeature as formatFeature };
export { _addToMap as addToMap };
export { _addForecastToMap as addForecastToMap };
export { _removeFromMap as removeFromMap };
export { _buildPreferences as buildPreferences };
export { _getLevelsForInitialLoad as getLevelsForInitialLoad };
export { _getLevelsFromPreferences as getLevelsFromPreferences };
export { _getOpacityFromPreferences as getOpacityFromPreferences };
export { _getHistograms as getHistograms };
export { _queryStabilityLayer as queryStabilityLayer };
export { _queryForecastStabilityLayer as queryForecastStabilityLayer };
export { _formatStabilityTileFeatureProperties as formatStabilityTileFeatureProperties };
export { _populateDefaultPreferences as populateDefaultPreferences };

const _getStability = async (params) => {
  try {
    let url;

    if (params.next) {
      url = params.next;
    } else {
      // set some defaults for stability
      params.gridType = GRIDS_TYPES.stability;
      params.verbose = true;
      params.local = true;
      params.pageSize = 1000;

      if (isFeatureUnlocked("pulse-api-v2")) {
        params.model = "v2";
      }

      url = encodeAllURLParameters({
        baseEndpoint: ENDPOINT_GRIDS,
        params: params,
      });
    }

    const RESULT = await httpGet({ url: url });
    const { data } = JSON.parse(RESULT);

    let stability = data.features;

    if (!params.ignoreNext) {
      if (!params.stability) {
        params.stability = [];
      }
      stability = data.features.concat(params.stability);
    } else {
      stability = data.features;
    }
    if (!params.ignoreNext && data.metadata && data.metadata.next) {
      params.next = data.metadata.next;
      params.stability = stability;
      await _getStability(params);
    }

    _formatFeatures(stability);

    return data;
  } catch (error) {
    Promise.reject("Issue getting stability in stability.service.js");
  }
};

const _getStabilityByGeohash = async (params) => {
  try {
    // set some defaults for stability
    params.gridType = GRIDS_TYPES.stability;
    params.dataLensType = GRIDS_DATA_TYPE_GDELT_ALL;
    params.local = true;

    if (isFeatureUnlocked("pulse-api-v2")) {
      params.model = "v2";
    }

    const url = encodeAllURLParameters({
      baseEndpoint: `${ENDPOINT_GRIDS}/geohash/${params.aoi}`,
      params: params,
    });

    const RESULT = await httpGet({ url: url });
    const { data } = JSON.parse(RESULT);

    if (
      data &&
      data.features &&
      Array.isArray(data.features) &&
      data.features[0]
    ) {
      _formatFeatures(data.features, params.stabilityType);
    }

    return data;
  } catch (error) {
    Promise.reject(
      "Issue getting stability by geohash in stability.service.js"
    );
  }
};

const _getStabilityByCountry = async (params) => {
  params.locationType = "country";
  return _getStabilityByCountryCityCounty(params);
};

const _getStabilityByCity = async (params) => {
  params.locationType = "city";
  return _getStabilityByCountryCityCounty(params);
};

const _getStabilityByCounty = async (params) => {
  params.locationType = "county";
  return _getStabilityByCountryCityCounty(params);
};

const _getStabilityByCountryCityCounty = async (params) => {
  try {
    // set some defaults for stability
    params.gridType = GRIDS_TYPES.stability;
    params.dataLensType = GRIDS_DATA_TYPE_GDELT_ALL;
    params.local = true;

    let type = params.stabilityType;
    if (!params.stabilityType && params.locationType) {
      type = params.locationType;
    }

    const AOI = params.aoi.toLowerCase();

    if (isFeatureUnlocked("pulse-api-v2")) {
      params.model = "v2";
    }

    const url = encodeAllURLParameters({
      baseEndpoint: `${ENDPOINT_GRIDS}/${type}/${AOI}`,
      params: params,
    });

    const RESULT = await httpGet({ url: url });
    const { data } = JSON.parse(RESULT);

    if (
      data &&
      data.features &&
      Array.isArray(data.features) &&
      data.features[0]
    ) {
      _formatFeatures(data.features, params.stabilityType);
    }

    return data;
  } catch (error) {
    Promise.reject(
      `Issue getting stability by ${params.locationType} in stability.service.js: ${error}`
    );
  }
};

const _formatFeatures = (features, type) => {
  const LEN = features.length;
  for (let i = 0; i < LEN; i++) {
    let feature = features[i];
    _formatFeature(feature, type);
  }
};

const _formatFeature = (feature, type) => {
  if (feature.properties) {
    if (!feature.properties._id) {
      feature.properties._id = feature.properties.geohash || null;
    }
    feature.properties.formatted = {};
    feature.properties.formatted.type = type;
    feature.properties.formatted.location = null;

    if (
      feature.geometry &&
      feature.geometry.coordinates &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length === 2
    ) {
      feature.properties.formatted.location = feature.geometry.coordinates;
    }
  }
};

const _addToMap = (params) => {
  const SOURCE_ID = params.sourceId
    ? params.sourceId
    : `${params.type}-source-${params.id}`;

  removeTileData({
    map: params.map,
    layerId: params.layerId,
    sourceId: SOURCE_ID,
  });

  let beforeLayer = LAYER_ID_ACETATE;
  if (params.map.getLayer("City large scale/town small")) {
    beforeLayer = "City large scale/town small";
  }

  let layerOptions = {
    id: params.layerId,
    metadata: params.metadata ?? params.layerId,
    source: SOURCE_ID,
    type: "fill",
    paint: {
      "fill-outline-color": "#4b4b4b",
      "fill-opacity":
        params.opacity || params.opacity === 0
          ? params.opacity
          : DEFAULT_OPACITY_PULSE,
    },
    before: beforeLayer,
  };

  const SCORE_FIELD = "co";

  if (params.filters) {
    const STYLE_PARAMS = {
      type: GRIDS_TYPES.stability,
      field: SCORE_FIELD,
      filters: params.filters,
    };
    layerOptions.paint["fill-color"] = getVarianceScoreColorStyle(STYLE_PARAMS);
  }

  if (params.aoiCode && params.aoiCodeField) {
    layerOptions.paint["fill-opacity"] = [
      "match",
      ["get", params.aoiCodeField],
      params.aoiCode,
      0.99,
      0.01,
    ];
    let fillColor = params.aoiCodeFillColor
      ? params.aoiCodeFillColor
      : "rgba(255, 255, 255, 0.01)";
    layerOptions.paint["fill-color"] = [
      "match",
      ["get", params.aoiCodeField],
      params.aoiCode,
      fillColor,
      "rgba(255, 255, 255, 0.01)",
    ];
    layerOptions.paint["fill-outline-color"] = [
      "match",
      ["get", params.aoiCodeField],
      params.aoiCode,
      "rgba(0, 174, 66, 0.99)",
      "rgba(255, 255, 255, 0.2)",
    ];
  }

  layerOptions["source-layer"] = TILES_SOURCE_LAYERS[params.type];

  const SOURCE = {
    type: "vector",
    url: MAP_TILE_SERVER_STATIC + "/" + TILES_SOURCE_NAMES[params.type],
  };

  let options = {
    id: SOURCE_ID,
    source: SOURCE,
    layerOptions: [layerOptions],
    map: params.map,
  };

  if (params.mapClickEvent) {
    options.mapClickEvent = params.mapClickEvent;
  }

  addTileData(options);
};

const _addForecastToMap = (params) => {
  const SOURCE_ID = params.sourceId
    ? params.sourceId
    : `${params.type}-forecast-source-${params.id}`;

  removeTileData({
    map: params.map,
    layerId: params.layerId,
    sourceId: SOURCE_ID,
  });

  let layerOptions = {
    id: params.layerId,
    source: SOURCE_ID,
    type: "fill",
    paint: {
      "fill-outline-color": "rgba(255, 255, 255, 0.2)",
      "fill-opacity":
        params.opacity || params.opacity === 0
          ? params.opacity
          : DEFAULT_OPACITY,
    },
    before: LAYER_ID_ACETATE, // just add it before acetate to make sure point layers are after it
  };

  const SCORE_FIELD = "count";

  if (params.filters) {
    const STYLE_PARAMS = {
      type: GRIDS_TYPES.stability,
      field: SCORE_FIELD,
      filters: params.filters,
    };
    layerOptions.paint["fill-color"] = getVarianceScoreColorStyle(STYLE_PARAMS);
    layerOptions.paint["fill-outline-color"] = getVarianceScoreColorStyle(
      STYLE_PARAMS
    );
  }

  layerOptions["source-layer"] = TILES_FORECAST_SOURCE_LAYERS[params.type];

  const SOURCE = {
    type: "vector",
    url: MAP_TILE_SERVER_STATIC + "/" + TILES_SOURCE_NAMES[params.type],
  };

  let options = {
    id: SOURCE_ID,
    source: SOURCE,
    layerOptions: [layerOptions],
    map: params.map,
  };

  if (params.mapClickEvent) {
    options.mapClickEvent = params.mapClickEvent;
  }

  addTileData(options);
};

const _removeFromMap = (params) => {
  if (!params.map) {
    //no map to remove the layer from
    return;
  }
  const LAYER = params.map.getLayer(params.layerId);
  if (LAYER) {
    const SOURCE_ID = LAYER.source;
    if (SOURCE_ID) {
      removeTileData({
        map: params.map,
        layerId: params.layerId,
        sourceId: SOURCE_ID,
      });
    }
  }
};

const _buildPreferences = (params) => {
  let preferences = lodashCloneDeep(params.preferences);

  preferences.grids = preferences.grids || {};
  preferences.grids.stability = preferences.grids.stability || {};

  if (params.levels) {
    let levels = [];
    params.levels.forEach((level) => {
      if (level.selected) {
        levels.push(level.id);
      }
    });
    preferences.grids.stability.levels = levels;
  }

  if (params.hasOwnProperty("type")) {
    preferences.grids.stability.type = params.type;
  }

  if (params.hasOwnProperty("opacity")) {
    preferences.grids.stability.opacity = params.opacity;
  }

  if (params.hasOwnProperty("enabled")) {
    preferences.grids.stability.enabled = params.enabled;
  }

  return preferences;
};

const _getLevelsForInitialLoad = () => {
  let levels = [];
  const LEVELS = lodashCloneDeep(GRIDS_STABILITY_LEVELS);
  LEVELS.forEach((level) => {
    level.selected = true;
    levels.push(level);
  });
  return levels;
};

const _getLevelsFromPreferences = (preferences) => {
  let levels = [];
  if (
    lodashGet(preferences, "grids.stability.levels") &&
    Array.isArray(preferences.grids.stability.levels)
  ) {
    const LEVELS = lodashCloneDeep(GRIDS_STABILITY_LEVELS);
    LEVELS.forEach((level) => {
      for (let i = 0; i < preferences.grids.stability.levels.length; i++) {
        let levelId = preferences.grids.stability.levels[i];
        if (levelId === level.id) {
          level.selected = true;
          break;
        }
      }
      level.selected = level.hasOwnProperty("selected")
        ? level.selected
        : false;
      levels.push(level);
    });
  } else {
    levels = _getLevelsForInitialLoad();
  }
  return levels;
};

const _getOpacityFromPreferences = (preferences) => {
  let opacity = 50;
  if (
    lodashGet(preferences, "grids.stability.opacity") ||
    lodashGet(preferences, "grids.stability.opacity") === 0
  ) {
    opacity = preferences.grids.stability.opacity;
  }
  return opacity;
};

const _getHistograms = async (params) => {
  try {
    let histograms = {};
    let result;
    params.omitGeometry = true;
    if (params.stabilityType === GRIDS_STABILITY_TYPES.country) {
      result = await _getStabilityByCountry(params);
    } else if (params.stabilityType === GRIDS_STABILITY_TYPES.region) {
      result = await _getStabilityByGeohash(params);
    } else if (params.stabilityType === GRIDS_STABILITY_TYPES.city) {
      result = await _getStabilityByCity(params);
    } else if (params.stabilityType === GRIDS_STABILITY_TYPES.county) {
      result = await _getStabilityByCounty(params);
    }

    if (result && result.metadata) {
      if (result.metadata.global_histogram) {
        histograms.global = result.metadata.global_histogram;
      }
      if (result.metadata.daily_histogram) {
        histograms.daily = result.metadata.daily_histogram;
      }
    }

    return histograms;
  } catch (error) {
    Promise.reject(
      "Unable to retrieve histograms in stability.service: " + error
    );
  }
};

const _formatHistogramScores = (length) => (entry, idx) => {
  const daysToAdd = (length - 1 - idx) * -1;
  const date = dayjs().add(daysToAdd, "days");
  return {
    key_as_string: date.toISOString(),
    key: date.valueOf(),
    count_sum: {
      value: entry, //Math.floor(entry)
    },
  };
};

const _formatStabilityTileFeatureProperties = (feature) => {
  if (
    lodashHas(feature, "properties.wh") &&
    typeof feature.properties.wh === "string"
  ) {
    const WEIGHTED_HISTORY = JSON.parse(feature.properties.wh);
    const SLICED = lodashSlice(WEIGHTED_HISTORY, -60);
    const MAPPED = lodashMap(SLICED, _formatHistogramScores(60));
    feature.properties.wh = MAPPED;
  }

  if (
    lodashHas(feature, "properties.tr") &&
    typeof feature.properties.tr === "string"
  ) {
    const HISTORY = JSON.parse(feature.properties.tr);
    const SLICED = lodashSlice(HISTORY, -60);
    const MAPPED = lodashMap(SLICED, _formatHistogramScores(60));
    feature.properties.tr = MAPPED;
  }

  if (
    lodashHas(feature, "properties.forecast") &&
    typeof feature.properties.forecast === "string"
  ) {
    feature.properties.forecast = JSON.parse(feature.properties.forecast);
  }

  if (
    lodashHas(feature, "properties.locations") &&
    typeof feature.properties.locations === "string"
  ) {
    feature.properties.locations = JSON.parse(feature.properties.locations);
  }

  if (
    lodashHas(feature, "properties.subscores") &&
    typeof feature.properties.subscores === "string"
  ) {
    feature.properties.subscores = JSON.parse(feature.properties.subscores);
  }

  return feature;
};

const _queryStabilityLayer = (params) => {
  const TYPE = params.type ? params.type : GRIDS_STABILITY_TYPES.region;
  if (params.map) {
    const FILTER = [
      "any",
      ["==", "_id", params.filterValue.toUpperCase()],
      ["==", "_id", params.filterValue.toLowerCase()],
    ];
    const FEATURES = params.map.querySourceFeatures(`source-pulse-${TYPE}`, {
      sourceLayer: TILES_SOURCE_LAYERS[TYPE],
      filter: FILTER,
    });

    return FEATURES.map(_formatStabilityTileFeatureProperties);
  }
  return null;
};

const _queryForecastStabilityLayer = (params) => {
  const TYPE = params.type ? params.type : GRIDS_STABILITY_TYPES.region;
  if (params.map) {
    let field = "";
    switch (TYPE) {
      case GRIDS_STABILITY_TYPES.country:
        field = "iso2";
        break;
      case GRIDS_STABILITY_TYPES.region:
      case GRIDS_STABILITY_TYPES.city:
      case GRIDS_STABILITY_TYPES.county:
      default:
        field = "_id";
        break;
    }

    const FILTER = [
      "any",
      ["==", field, params.filterValue.toUpperCase()],
      ["==", field, params.filterValue.toLowerCase()],
    ];

    const FEATURES = params.map.querySourceFeatures(
      `source-forecast-pulse-${TYPE}`,
      {
        sourceLayer: TILES_FORECAST_SOURCE_LAYERS[TYPE],
        filter: FILTER,
      }
    );

    return FEATURES.map(_formatStabilityTileFeatureProperties).slice(0, 1);
  }
  return null;
};

const _populateDefaultPreferences = (prefs = {}) => {
  if (!prefs.grids) {
    prefs.grids = {};
  }
  if (!prefs.grids.stability) {
    prefs.grids.stability = {};
  }
  if (!prefs.grids.stability.type && prefs.grids.stability.type !== null) {
    prefs.grids.stability.type = GRIDS_STABILITY_TYPES.country;
  }
  if (!prefs.grids.stability.levels) {
    prefs.grids.stability.levels = GRIDS_STABILITY_LEVELS.map(
      (stabilityLevel) => {
        return stabilityLevel.id;
      }
    );
  }

  return prefs;
};

const _getStabilityDelta = async (params) => {
  try {
    // set some defaults for stability
    const locationType = params.locationType.toLowerCase();

    if (isFeatureUnlocked("pulse-api-v2")) {
      params.model = "v2";
    }

    const url = encodeAllURLParameters({
      baseEndpoint: `${ENDPOINT_GRIDS_DELTA}/${locationType}`,
      params: params,
    });
    const RESULT = await httpGet({ url: url });
    const { data } = JSON.parse(RESULT);

    if (
      data &&
      data.features &&
      Array.isArray(data.features) &&
      data.features[0]
    ) {
      _formatFeatures(data.features, params.stabilityType);
    }

    return data;
  } catch (error) {
    Promise.reject(
      `Issue getting pulse delta by ${params.locationType} in stability.service.js: ${error}`
    );
  }
};
